// User management API client (ZTAI-native local users)

import { Role } from '../types/auth';

export interface ManagedUser {
  userId: string;
  orgId: string;
  email?: string | null;
  displayName?: string | null;
  role: Role;
  active: boolean;
  mustChangePassword: boolean;
  canApproveActions: boolean;
  team?: string | null;
  createdAt?: string;
  lastLogin?: string | null;
}

export interface CreateUserInput {
  userId: string;
  password: string;
  role: Role;
  displayName?: string;
  email?: string;
  mustChangePassword?: boolean;
}

async function parseError(res: Response): Promise<never> {
  const err = await res.json().catch(() => ({}));
  throw new Error(err.error || err.message || `Request failed (${res.status})`);
}

export async function listUsers(apiUrl: string): Promise<ManagedUser[]> {
  const res = await fetch(`${apiUrl}/api/admin/users`);
  if (!res.ok) return parseError(res);
  const data = await res.json();
  return data.users || [];
}

export async function createUser(apiUrl: string, input: CreateUserInput): Promise<ManagedUser> {
  const res = await fetch(`${apiUrl}/api/admin/users`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  if (!res.ok) return parseError(res);
  return (await res.json()).user;
}

export async function updateUser(
  apiUrl: string,
  userId: string,
  fields: { role?: Role; displayName?: string; email?: string }
): Promise<void> {
  const res = await fetch(`${apiUrl}/api/admin/users/${encodeURIComponent(userId)}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(fields),
  });
  if (!res.ok) return parseError(res);
}

export async function deleteUser(apiUrl: string, userId: string): Promise<void> {
  const res = await fetch(`${apiUrl}/api/admin/users/${encodeURIComponent(userId)}`, {
    method: 'DELETE',
  });
  if (!res.ok) return parseError(res);
}

export async function setUserActive(apiUrl: string, userId: string, active: boolean): Promise<void> {
  const res = await fetch(`${apiUrl}/api/admin/users/${encodeURIComponent(userId)}/active`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ active }),
  });
  if (!res.ok) return parseError(res);
}

export async function resetUserPassword(apiUrl: string, userId: string, password: string): Promise<void> {
  const res = await fetch(`${apiUrl}/api/admin/users/${encodeURIComponent(userId)}/reset-password`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ password }),
  });
  if (!res.ok) return parseError(res);
}

/** Self-service password change for the logged-in user. */
export async function changeMyPassword(
  apiUrl: string,
  newPassword: string,
  currentPassword?: string
): Promise<void> {
  const res = await fetch(`${apiUrl}/api/admin/user/change-password`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ newPassword, currentPassword }),
  });
  if (!res.ok) return parseError(res);
}
